import React from 'react'
import { Modal } from '@/shared/ui/Modal'
import { Button, Input, Label, Radio, Switch } from '@/shared/ui'
import { isAffiliateCodeAvailable, createAffiliateByEmail } from '../services/affiliateService'
import AffiliateUplineFields from './AffiliateUplineFields'

function slugify(s: string) {
  return s
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/(^-|-$)+/g, '')
    .toLowerCase()
}

async function generateCodeBase(hint: string) {
  const base = slugify(hint || '') || 'afiliado'
  for (let i = 0; i < 10; i++) {
    const suffix = Math.random().toString(36).slice(2, 6)
    const cand = `${base}-${suffix}`
    // eslint-disable-next-line no-await-in-loop
    const ok = await isAffiliateCodeAvailable(cand)
    if (ok) return cand
  }
  return `${base}-${Date.now().toString(36).slice(-4)}`
}

type Props = {
  open: boolean
  onClose: () => void
  onCreated?: () => void
}

export default function CreateAffiliateModal({ open, onClose, onCreated }: Props) {
  const [name, setName] = React.useState('')
  const [email, setEmail] = React.useState('')
  const [code, setCode] = React.useState('')
  const [commissionMode, setCommissionMode] = React.useState<'percent'|'fixed'>('percent')
  const [commissionValue, setCommissionValue] = React.useState<number>(10)
  const [recurring, setRecurring] = React.useState(true)
  const [parentAffiliateId, setParentAffiliateId] = React.useState<string | null>(null)
  const [uplinePercent, setUplinePercent] = React.useState<number>(0)
  const [submitting, setSubmitting] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (open) {
      setName('')
      setEmail('')
      setCode('')
      setCommissionMode('percent')
      setCommissionValue(10)
      setRecurring(true)
      setParentAffiliateId(null)
      setUplinePercent(0)
      setSubmitting(false)
      setError(null)
    }
  }, [open])

  async function onGenerateCode() {
    const hint = email || name
    const cand = await generateCodeBase(hint)
    setCode(cand)
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    try {
      if (!email.trim() || !name.trim() || !code.trim()) throw new Error('Preencha nome, e-mail e código')
      setSubmitting(true)
      await createAffiliateByEmail({
        name: name.trim(),
        email: email.trim().toLowerCase(),
        code: code.trim(),
        commissionMode,
        commissionValue: Number(commissionValue) || 0,
        recurrence: recurring ? 'recurring' : 'one_time',
        parentAffiliateId,
        uplineCommissionPercent: uplinePercent || 0,
      } as any)
      setSubmitting(false)
      onClose()
      onCreated?.()
    } catch (err: any) {
      setSubmitting(false)
      setError(err?.message ?? 'Falha ao cadastrar afiliado')
    }
  }

  return (
    <Modal open={open} onOpenChange={(v)=>{ if(!v) onClose() }} className="p-8">
      <div className="mb-4 text-2xl font-semibold tracking-tight">Novo afiliado</div>
      <form className="space-y-6" onSubmit={onSubmit}>
        {error && (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 p-2 text-sm text-destructive-foreground">{error}</div>
        )}
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="aff-name">Nome</Label>
            <Input id="aff-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Nome completo" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="aff-email">E-mail</Label>
            <Input id="aff-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email@dominio.com" />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="aff-code">Código de afiliado</Label>
          <div className="flex gap-2">
            <Input id="aff-code" value={code} onChange={(e) => setCode(e.target.value)} placeholder="ex.: nome-xyz1" />
            <Button type="button" variant="outline" onClick={onGenerateCode}>Gerar</Button>
          </div>
          <p className="text-xs text-foreground/70">Este código compõe o link de afiliado (ex.: /ref/codigo).</p>
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Comissão</Label>
            <div className="flex items-center gap-4">
              <Radio name="mode" checked={commissionMode==='percent'} onChange={() => setCommissionMode('percent')} label="Percentual (%)" />
              <Radio name="mode" checked={commissionMode==='fixed'} onChange={() => setCommissionMode('fixed')} label="Valor fixo" />
            </div>
            <Input
              type="number"
              min="0"
              step={commissionMode==='percent' ? '0.1' : '0.01'}
              value={commissionValue}
              onChange={(e) => setCommissionValue(parseFloat(e.target.value))}
              placeholder={commissionMode==='percent' ? 'ex.: 10 para 10%' : 'ex.: 49.90'}
            />
          </div>
          <div className="space-y-2">
            <Label>Recorrência</Label>
            <div className="flex items-center gap-2">
              <Switch checked={recurring} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setRecurring(e.currentTarget.checked)} />
              <span className="text-sm">Pagar a cada renovação do plano</span>
            </div>
            <p className="text-sm text-foreground/70">Desligue para pagamento único na primeira venda.</p>
          </div>
        </div>

        <AffiliateUplineFields
          currentAffiliateId={null}
          parentAffiliateId={parentAffiliateId}
          onParentChange={setParentAffiliateId}
          uplinePercent={uplinePercent}
          onUplinePercentChange={setUplinePercent}
        />

        <div className="mt-6 flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>Cancelar</Button>
          <Button type="submit" disabled={submitting}>{submitting ? 'Cadastrando...' : 'Cadastrar'}</Button>
        </div>
      </form>
    </Modal>
  )
}
